/**
 * DocumentsPanel — proposals & contracts attached to any entity.
 *
 * Self-contained: subscribes to the `documents` collection itself and
 * filters to the entity it's handed, so it can drop into the account
 * detail modal, the prospect detail pane, or anywhere else with a
 * stable (entityType, entityId) pair. Generates a templated body on
 * "New proposal" / "New contract", then lets you edit inline.
 */

import { useEffect, useState } from 'react';
import {
  ChevronDown,
  Download,
  FileSignature,
  FileText,
  Printer,
  Trash2,
} from 'lucide-react';
import { C } from '../lib/tokens';
import {
  createDocument,
  deleteDocument,
  setDocumentStatus,
  subscribeDocuments,
  updateDocument,
  STATUSES_BY_KIND,
} from '../lib/db/documents';
import { renderBrandedDocHtml } from '../lib/docs/brandDoc';
import { useAuth } from '../lib/auth/AuthProvider';
import type {
  BusinessDoc,
  DocumentEntityType,
  DocumentKind,
} from '../lib/db/types';

interface DocumentsPanelProps {
  entityType: DocumentEntityType;
  entityId: string;
  entityName: string;
  /** Person to address the document to (defaults to entityName). */
  contactName?: string;
  /** Provider/owner shown on the document. */
  ownerName?: string;
  /** Seeds the dollar value on generated documents. */
  defaultValue?: number;
  /**
   * When provided, the panel renders these (already filtered to the
   * entity) instead of subscribing itself. Lets a parent that already
   * holds a subscription avoid opening a second one.
   */
  documents?: BusinessDoc[];
}

export function DocumentsPanel({
  entityType,
  entityId,
  entityName,
  contactName,
  ownerName,
  defaultValue,
  documents: controlled,
}: DocumentsPanelProps) {
  const { user } = useAuth();
  const canMutate = user?.role !== 'viewer';
  const [internal, setInternal] = useState<BusinessDoc[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (controlled) return; // parent-controlled: skip our own subscription
    const unsub = subscribeDocuments((all) => {
      setInternal(
        all
          .filter((d) => d.entityType === entityType && d.entityId === entityId)
          .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
      );
    });
    return unsub;
  }, [entityType, entityId, controlled]);

  const documents = controlled ?? internal;

  const generate = async (kind: DocumentKind) => {
    setBusy(true);
    try {
      const doc = await createDocument({
        kind,
        entityType,
        entityId,
        entityName,
        contactName,
        ownerName,
        value: defaultValue,
      });
      setOpenId(doc.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <p className="t-body" style={{ color: C.textMuted, margin: 0 }}>
          Proposals and contracts for {entityName}.
          {canMutate ? ' Generated from a template, then editable inline.' : ''}
        </p>
        {canMutate && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="glass-btn"
              onClick={() => generate('proposal')}
              disabled={busy}
              title="Generate a proposal"
            >
              <FileText size={13} /> New proposal
            </button>
            <button
              className="glass-btn glass-btn--primary"
              onClick={() => generate('contract')}
              disabled={busy}
              title="Generate a contract"
            >
              <FileSignature size={13} /> New contract
            </button>
          </div>
        )}
      </div>

      {documents.length === 0 && (
        <div
          className="t-meta"
          style={{
            padding: '24px 12px',
            textAlign: 'center',
            border: '1px dashed rgba(255,255,255,0.08)',
            borderRadius: 12,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <FileText size={20} strokeWidth={1.75} />
          {canMutate
            ? 'No documents yet. Generate a proposal or contract above.'
            : 'No documents yet.'}
        </div>
      )}

      {documents.map((doc) => (
        <DocumentRow
          key={doc.id}
          doc={doc}
          open={openId === doc.id}
          onToggle={() => setOpenId((id) => (id === doc.id ? null : doc.id))}
          canMutate={canMutate}
        />
      ))}
    </div>
  );
}

export function DocumentRow({
  doc,
  open,
  onToggle,
  canMutate = true,
}: {
  doc: BusinessDoc;
  open: boolean;
  onToggle: () => void;
  canMutate?: boolean;
}) {
  const [body, setBody] = useState(doc.body);
  const [title, setTitle] = useState(doc.title);
  const [bodyDirty, setBodyDirty] = useState(false);

  // Reflect upstream changes when not mid-edit.
  useEffect(() => {
    if (!bodyDirty) {
      setBody(doc.body);
      setTitle(doc.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.body, doc.title]);

  const accent = doc.kind === 'proposal' ? C.magenta : C.blueElectric;
  const statuses = STATUSES_BY_KIND[doc.kind];

  const saveBody = async () => {
    await updateDocument(doc.id, { body, title });
    setBodyDirty(false);
  };

  const download = () => {
    const blob = new Blob([body], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${doc.title.replace(/[^\w- ]+/g, '').trim() || doc.kind}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /** Open the branded HTML in a new tab, ready to print to PDF. */
  const exportPdf = () => {
    const html = renderBrandedDocHtml({ ...doc, body, title });
    const win = window.open('', '_blank');
    if (!win) {
      window.alert('Allow pop-ups to preview & export this document.');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${doc.title}"? This can't be undone.`)) return;
    await deleteDocument(doc.id);
  };

  return (
    <div
      style={{
        borderRadius: 12,
        border: '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(255,255,255,0.04)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        className="glass-row"
        style={{
          width: '100%',
          textAlign: 'left',
          padding: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: 'transparent',
          border: 'none',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            flexShrink: 0,
            display: 'inline-grid',
            placeItems: 'center',
            color: accent,
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.10)',
          }}
        >
          {doc.kind === 'proposal' ? (
            <FileText size={15} />
          ) : (
            <FileSignature size={15} />
          )}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            className="t-card-title"
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {doc.title}
          </span>
          <span className="t-meta">
            {doc.kind === 'proposal' ? 'Proposal' : 'Contract'}
            {doc.value != null
              ? ` · ${doc.value.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 0,
                })}`
              : ''}{' '}
            · Updated {fmtAbsolute(doc.updatedAt)}
          </span>
        </span>
        <span
          className="glass-pill"
          style={{
            color: accent,
            borderColor: 'rgba(255,255,255,0.18)',
            background: 'rgba(255,255,255,0.04)',
            fontWeight: 700,
            textTransform: 'capitalize',
          }}
        >
          {doc.status}
        </span>
        <ChevronDown
          size={14}
          style={{
            color: C.textDim,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s ease',
          }}
        />
      </button>

      {open && (
        <div
          style={{
            padding: 12,
            display: 'grid',
            gap: 10,
            borderTop: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 160px auto',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <input
              className="glass-input"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setBodyDirty(true);
              }}
              placeholder={canMutate ? 'Document title' : undefined}
              readOnly={!canMutate}
            />
            <select
              className="glass-input"
              value={doc.status}
              onChange={(e) =>
                setDocumentStatus(doc.id, e.target.value as BusinessDoc['status'])
              }
              style={{ fontFamily: 'Manrope, sans-serif' }}
              title="Status"
              disabled={!canMutate}
            >
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className="glass-btn glass-btn--primary"
                onClick={exportPdf}
                title="Preview & export branded PDF"
              >
                <Printer size={13} /> Export PDF
              </button>
              <button className="glass-btn" onClick={download} title="Download .md">
                <Download size={13} />
              </button>
              {canMutate && (
                <button className="glass-btn" onClick={remove} title="Delete">
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          </div>
          <textarea
            className="glass-input"
            value={body}
            rows={14}
            onChange={(e) => {
              setBody(e.target.value);
              setBodyDirty(true);
            }}
            readOnly={!canMutate}
            style={{
              width: '100%',
              padding: '10px 12px',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 12.5,
              lineHeight: 1.6,
            }}
          />
          {canMutate && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className="glass-btn glass-btn--primary"
                onClick={saveBody}
                disabled={!bodyDirty}
              >
                Save document
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function fmtAbsolute(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
